import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

import { QuestionFilters, type QuestionFiltersProps } from '../../organisms/QuestionFilters';
import { QuestionTable, type QuestionTableProps } from '../../organisms/QuestionTable';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/**
 * Props do template da Biblioteca de Questões.
 * Recebe tudo via slots — sem dados reais ou chamadas à API.
 */
export interface QuestionLibraryTemplateProps {
  /** Props repassadas para o QuestionTable */
  readonly tableProps: QuestionTableProps;
  /** Props repassadas para o QuestionFilters */
  readonly filterProps: QuestionFiltersProps;
  /** Callback ao clicar em "Adicionar Questão" */
  readonly onAddQuestion: () => void;
  /** Slot para modais (criação/edição) */
  readonly modal?: ReactNode;
  /** Total de registros filtrados */
  readonly totalCount: number;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Template da Biblioteca de Questões.
 *
 * Define a estrutura visual sem dados reais:
 * - Breadcrumb + PageHeader com ação de adicionar questão
 * - Painel de filtros
 * - Contador de resultados
 * - Tabela de questões com paginação
 *
 * Sem lógica de negócio — recebe tudo via props.
 *
 * @example
 * ```tsx
 * <QuestionLibraryTemplate
 *   tableProps={{ data, isLoading, totalCount, currentPage, totalPages, onPageChange }}
 *   filterProps={{ filters, authorOptions, companyOptions, onFiltersChange, onSearch, onClear }}
 *   onAddQuestion={() => navigate({ to: '/question-library/new' })}
 *   totalCount={totalCount}
 * />
 * ```
 */
export function QuestionLibraryTemplate({
  tableProps,
  filterProps,
  onAddQuestion,
  modal,
}: QuestionLibraryTemplateProps) {
  const { t } = useTranslation('questionLibrary');

  return (
    <div className="flex flex-col gap-sm">
      {/* Header */}
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('title')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={
          <Button hierarchy="primary" size="md" leadingIcon={PlusIcon} onClick={onAddQuestion}>
            {t('actions.addQuestion')}
          </Button>
        }
      />

      {/* Filtros */}
      <section aria-label={t('filters.label')}>
        <QuestionFilters {...filterProps} />
      </section>

      {/* Tabela */}
      <section className="shadow-level1" aria-label={t('table.label')}>
        <QuestionTable {...tableProps} />
      </section>

      {/* Modais */}
      {modal}
    </div>
  );
}
